<template>
  <Container v-if="!pending && data" class="bg-surface-default-low px-16 pt-24">
    <RevForm :initial-values name="gift-cards-selection" @submit="handleSubmit">
      <template #default="{ values }">
        <div class="block gap-64 md:flex">
          <div
            class="sticky top-[168px] hidden max-w-[50%] self-start md:block"
          >
            <DesignPickers
              v-model="values.design"
              :design-pickers="data.designPickers"
            />
          </div>

          <div>
            <div class="mb-32 space-y-32">
              <div class="space-y-8">
                <h2 class="text-static-default-hi heading-1">
                  {{ i18n(translations.title) }}
                </h2>
                <div
                  ref="checkoutStickyBarVisibilityBeacon"
                  class="text-static-default-hi body-2"
                >
                  {{ i18n(translations.subtitle) }}
                </div>
              </div>

              <DesignPickers
                v-model="values.design"
                class="md:hidden"
                :design-pickers="data.designPickers"
              />

              <ReassuranceItems />

              <PricePickers
                v-model="values.price"
                :max-price="data.customAmount.maximum"
                :min-price="data.customAmount.minimum"
              />

              <SelectedDesign
                class="md:hidden"
                :design-pickers="data.designPickers"
                :model-value="values.design"
              />

              <div class="pt-32">
                <Proposal :price="values.price" />

                <RevButton
                  class="mt-12 w-full"
                  :loading
                  type="submit"
                  variant="primary"
                  >{{ i18n(translations.checkout) }}
                </RevButton>

                <PaymentIcons
                  class="my-24"
                  :methods="[PaymentMethodCode.CARD]"
                />

                <FormattedMessage
                  class="text-static-default-low caption"
                  :definition="translations.termsAndConditions"
                >
                  <template #termsAndConditionsLink>
                    <RevLink :to="i18n(translations.termsAndConditionsLink)">
                      {{ i18n(translations.termsAndConditionsLinkLabel) }}
                    </RevLink>
                  </template>
                </FormattedMessage>
              </div>

              <ReassuranceItems />

              <StickyBarBottom
                :loading
                :price="values.price"
                :show-bar="!isCheckoutStickyBarInViewport"
                @submit="handleSubmit"
              />
            </div>
          </div>
        </div>
      </template>
    </RevForm>

    <FaqDrawer />
  </Container>
</template>

<script setup lang="ts">
import { useAsyncData } from '#imports'
import { ref, useTemplateRef } from 'vue'

import type { Currency } from '@backmarket/http-api'
import { PaymentMethodCode } from '@backmarket/http-api/src/api-specs-payment/payment/payment.types'
import { PaymentIcons } from '@backmarket/nuxt-layer-payment/methods'
import type { ApiSpecs } from '@backmarket/nuxt-module-http-v2/specs'
import { useHttpFetch } from '@backmarket/nuxt-module-http-v2/useHttpFetch'
import FormattedMessage from '@backmarket/nuxt-module-i18n/FormattedMessage.vue'
import { useI18n } from '@backmarket/nuxt-module-i18n/useI18n'
import { useMarketplace } from '@backmarket/nuxt-module-marketplace/useMarketplace'
import { useTheToast } from '@backmarket/nuxt-module-toast/useTheToast'
import { RevButton } from '@ds/components/Button'
import { RevForm } from '@ds/components/Form'
import { RevLink } from '@ds/components/Link'
import { useElementVisibility } from '@vueuse/core'

import Container from '~/scopes/product/components/Container/Container.vue'

import translations from './GiftCards.translations'
import DesignPickers from './components/DesignPickers/DesignPickers.vue'
import FaqDrawer from './components/FaqDrawer/FaqDrawer.vue'
import PricePickers from './components/PricePickers/PricePickers.vue'
import Proposal from './components/Proposal/Proposal.vue'
import ReassuranceItems from './components/ReassuranceItems/ReassuranceItems.vue'
import SelectedDesign from './components/SelectedDesign/SelectedDesign.vue'
import StickyBarBottom from './components/StickyBarBottom/StickyBarBottom.vue'

const i18n = useI18n()
const $httpFetch = useHttpFetch()
const loading = ref(false)
const { openErrorToast } = useTheToast()

type giftCardForm =
  ApiSpecs['/api/gift-cards/v1/product-page/giftcard/add']['POST']['body']

const { data, pending } = await useAsyncData(() =>
  $httpFetch('/api/gift-cards/v1/product-page/giftcard', {
    method: 'GET',
  }),
)

const {
  market: { defaultCurrency },
} = useMarketplace()

const checkoutStickyBarVisibilityBeacon = useTemplateRef<HTMLDivElement>(
  'checkoutStickyBarVisibilityBeacon',
)
const isCheckoutStickyBarInViewport = useElementVisibility(
  checkoutStickyBarVisibilityBeacon,
)

const initialValues = {
  design: '1',
  price: {
    amount: '20',
    currency: defaultCurrency as Currency,
  },
  recipientName: 'recipient',
  senderName: 'sender',
  message: '',
  // recipientName and senderName are not implmented yet on frontend but are required on backend
}

async function handleSubmit(values: giftCardForm) {
  loading.value = true
  try {
    await $httpFetch('/api/gift-cards/v1/product-page/giftcard/add', {
      method: 'POST',
      body: values,
    })
  } catch (error) {
    openErrorToast()
  } finally {
    loading.value = false
  }
}
</script>
